import React from 'react';
import { imageConfig, getImage } from '../../config/images';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallback?: string;
  lazy?: boolean;
  responsive?: boolean;
  className?: string;
}

export const Image: React.FC<ImageProps> = ({
  src,
  alt,
  fallback,
  lazy = true,
  responsive = true,
  className = '',
  ...props
}) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = fallback || imageConfig.placeholders.default;
  };

  const imageClasses = `
    ${responsive ? 'max-w-full h-auto' : ''}
    ${className}
  `.trim();

  return (
    <img
      src={getImage(src, fallback)}
      alt={alt}
      className={imageClasses}
      loading={lazy ? 'lazy' : 'eager'}
      onError={handleImageError}
      {...props}
    />
  );
};

// Specialized components for common image types
export const LogoImage: React.FC<Omit<ImageProps, 'fallback'>> = (props) => (
  <Image {...props} fallback={imageConfig.placeholders.logo} />
);

export const AvatarImage: React.FC<Omit<ImageProps, 'fallback'>> = (props) => (
  <Image {...props} fallback={imageConfig.placeholders.avatar} />
);

export const HeroImage: React.FC<Omit<ImageProps, 'fallback'>> = (props) => (
  <Image {...props} fallback={imageConfig.placeholders.landscape} />
);

// Background image component for sections
interface BackgroundImageProps {
  src: string;
  children: React.ReactNode;
  className?: string;
  overlay?: boolean;
  overlayOpacity?: number;
}

export const BackgroundImage: React.FC<BackgroundImageProps> = ({
  src,
  children,
  className = '',
  overlay = false,
  overlayOpacity = 0.5
}) => {
  const bgImageStyle = {
    backgroundImage: `url('${getImage(src)}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  return (
    <div 
      className={`relative ${className}`}
      style={bgImageStyle}
    >
      {overlay && (
        <div 
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
      )}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};